import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { banners, comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function HomePage() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotComics = comics.filter((c) => c.rating >= 4.7).slice(0, 6);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
  ).slice(0, 8);
  const completedComics = comics.filter((c) => c.status === '已完结').slice(0, 4);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '古风漫画网',
    url: 'https://www.gufengmanhua.com',
    description: '古风漫画网不仅提供免费的韩国漫画在线阅读，还独具特色地推荐兼具古风韵味的热门韩漫。采用流畅的下拉式观看模式，让您在欣赏精美画面的同时，享受便捷舒适的阅读体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.gufengmanhua.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '古风漫画网',
    url: 'https://www.gufengmanhua.com',
    description: '免费韩国漫画在线阅读平台，以古风美学为特色，提供热门韩漫下拉式观看体验。',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

      <section className="relative h-[500px] md:h-[600px] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentBanner ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={banner.imageUrl}
              alt={banner.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
          </div>
        ))}

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-10">
          <div className="w-full max-w-3xl">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
              {banners[currentBanner].title}
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
              {banners[currentBanner].subtitle}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link
                to={`/article/${banners[currentBanner].comicId}`}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                立即阅读
              </Link>
              <Link
                to={`/category/${categories[0].slug}`}
                className="px-6 py-2.5 bg-background-50/20 backdrop-blur-sm text-background-50 rounded-md font-medium text-sm hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                浏览更多
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBanner(index)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                index === currentBanner
                  ? 'bg-background-50 w-6'
                  : 'bg-background-50/50 hover:bg-background-50/80'
              }`}
              aria-label={`切换到第${index + 1}张横幅`}
            />
          ))}
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6">
        <section className="py-10 md:py-14">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center p-4 md:p-5 bg-background-100 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all mb-3">
                  <i className="ri-book-open-line text-lg md:text-xl"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
                <span className="text-xs text-foreground-400 mt-1">{cat.count}部</span>
              </Link>
            ))}
          </div>
        </section>

        <section className="py-8 md:py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              热门推荐
            </h2>
            <Link
              to={`/category/${categories[0].slug}`}
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {hotComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        <section className="py-8 md:py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              最近更新
            </h2>
            <Link
              to={`/category/${categories[0].slug}`}
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {latestComics.map((comic) => (
              <div key={comic.id} className="group cursor-pointer">
                <Link to={`/article/${comic.id}`} className="flex gap-3">
                  <div className="w-16 h-20 md:w-20 md:h-[104px] rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                      title={`${comic.title} - 古风漫画网`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1.5">{comic.author}</p>
                    <p className="text-xs text-foreground-400 mt-1">
                      更新至 {comic.totalChapters}话
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">{comic.updateTime}</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="py-8 md:py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              已完结佳作
            </h2>
            <Link
              to={`/category/${categories[0].slug}`}
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {completedComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} size="large" />
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-100 rounded-xl px-6 md:px-10 mb-10">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              享受古风美学 · 畅读热门韩漫
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              古风漫画网汇集了最热门的韩国漫画资源，以独特的古风美学设计为您打造沉浸式阅读体验。
              无论是奇幻冒险、恋爱言情还是悬疑推理，都在这里等您探索。采用流畅的下拉式观看模式，
              让您在欣赏精美画面的同时，享受舒适便捷的免费阅读之旅。
            </p>
            <Link
              to={`/category/${categories[0].slug}`}
              className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始阅读
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}